import React from 'react';
import '../../styles/components/ModalBodyAppInfo.scss';
import { Button, getIconComponent } from '../Carbon';
import { language } from '../../services/LanguageService';
import { AsperaWrapper } from '../AsperaWrapper';
import { AsperaDataTable } from '../AsperaDataTable';
import { errorPrefix } from '../../constants/constants';
import { getAppData, downloadSupportData } from '../../helpers/starterHelpers';
import { modal } from '../../services/ModalService';
import { SubManager } from '../../classes/SubManager';
import { AsperaError } from '../../classes/AsperaError';
import { notification } from '../../services/NotificationService';
import { AsperaCodeSnippet } from '../AsperaCodeSnippet';
import { AsperaAccordion, AsperaAccordionItem } from '../AsperaAccordion';
import { getEnvironmentVar } from '../../helpers/helpers';
export class ModalCopyError extends React.Component {
    componentWillUnmount() {
        const { modalClose } = this.props;
        modalClose({});
    }
    render() {
        const { errorData } = this.props;
        return React.createElement(AsperaCodeSnippet, { type: "multi", className: "modal-copy-error", value: errorData });
    }
}
/**
 * React Component
 * @remarks this modal is not exported and is used only for Aspera Starter internal system.
 *
 * The ModalBodyAppInfo provides the modal for viewing app information for running apps.
 *
 * @example
 * ```javascript
 * <ModalBodyAppInfo />
 * ```
 */
export class ModalBodyAppInfo extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            loading: true,
        };
        this.subManager = new SubManager();
        this.appData = [];
        this.errorHeaderData = [
            {
                name: language.get('modalAppInfo.errorName'),
                key: 'message',
                importance: 'primary',
                formatter: (data) => {
                    if (typeof data !== 'string')
                        return '';
                    return data.replace(`${errorPrefix}: `, '');
                },
            },
            {
                name: language.get('modalAppInfo.date'),
                key: 'date',
                width: 200,
                secondaryKey: 'date',
                importance: 'secondary',
                formatter: (date) => language.getDate(date, 'mediumDate'),
                secondaryFormatter: (date) => language.getDate(date, 'mediumTime'),
            },
        ];
        this.dataHeaderData = [
            {
                key: 'label',
                width: 220,
                importance: 'primary',
            },
            {
                key: 'value',
                importance: 'primary',
                formatter: (value) => {
                    return value || language.get('modalAppInfo.unknown');
                },
            },
        ];
        this.errorMainActions = [
            {
                name: language.get('modalAppInfo.consoleAllErrors'),
                kind: 'tertiary',
                hidden: this.desktopMode,
                icon: getIconComponent('Debug'),
                onClick: () => {
                    console.error(window.asperaLog || []);
                },
            },
        ];
        this.getErrorOverflow = (row) => {
            return [
                {
                    name: language.get('modalAppInfo.viewError'),
                    onClick: (row) => {
                        var _a, _b, _c, _d, _e, _f, _g, _h;
                        const modalData = modal.prepare();
                        const modalProps = {
                            modalHeading: language.get('modalAppInfo.viewError'),
                            modalLabel: row.message || '',
                            passiveModal: true,
                        };
                        try {
                            const errorData = JSON.stringify(((_c = (_b = (_a = row === null || row === void 0 ? void 0 : row.data) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.response) === null || _c === void 0 ? void 0 : _c.data) || ((_e = (_d = row === null || row === void 0 ? void 0 : row.data) === null || _d === void 0 ? void 0 : _d.error) === null || _e === void 0 ? void 0 : _e.response) || ((_g = (_f = row === null || row === void 0 ? void 0 : row.data) === null || _f === void 0 ? void 0 : _f.response) === null || _g === void 0 ? void 0 : _g.data) || ((_h = row === null || row === void 0 ? void 0 : row.data) === null || _h === void 0 ? void 0 : _h.response) || (row === null || row === void 0 ? void 0 : row.data) || row, undefined, 2);
                            this.subManager.setSubscription(modal.create(modalData.id, modalProps, React.createElement(ModalCopyError, { errorData: errorData, modalClose: modalData.callback })).subscribe({ next: () => { }, error: () => { } }));
                        }
                        catch (error) {
                            new AsperaError('ModalBodyAppInfo: Unable to stringify an error', error);
                            notification.create('error', language.get('modalAppInfo.unableToViewError'), language.get('modalAppInfo.unableToViewErrorHelper'));
                        }
                    },
                    disabled: !row.data,
                    icon: getIconComponent('CopyFile'),
                },
                {
                    name: language.get('modalAppInfo.consoleError'),
                    onClick: (row) => {
                        console.error(row.message);
                        if (row.data)
                            console.error(row.data);
                    },
                    hidden: this.desktopMode,
                    disabled: !row.data,
                    icon: getIconComponent('Debug'),
                },
            ];
        };
    }
    get desktopMode() {
        return !!getEnvironmentVar('desktop_mode');
    }
    componentWillUnmount() {
        const { modalClose } = this.props;
        this.subManager.unsubscribeAll();
        modalClose({});
    }
    componentDidMount() {
        this.appData = getAppData();
        this.setState({ loading: false });
    }
    render() {
        const { loading } = this.state;
        const { className, expandErrors } = this.props;
        const errors = (window.asperaLog || []).slice().reverse();
        const errorTable = React.createElement(AsperaDataTable, { tableId: "app-info-errors", emptyProps: { title: language.get('modalAppInfo.noErrors'), body: language.get('modalAppInfo.noErrorsBody') }, staticRowData: errors, headerData: this.errorHeaderData, disableFilter: true, tableActions: this.errorMainActions, getOverflowMenu: this.getErrorOverflow });
        let children = (React.createElement(AsperaAccordion, null,
            React.createElement(AsperaAccordionItem, { title: language.get('modalAppInfo.appData') },
                React.createElement(AsperaDataTable, { tableId: "app-info-data", staticRowData: this.appData.filter(data => !data.hidden), headerData: this.dataHeaderData, disableFilter: true })),
            React.createElement(AsperaAccordionItem, { title: language.get('modalAppInfo.sessionErrors'), open: expandErrors },
                React.createElement("p", null, language.get('modalAppInfo.consoleInstructions')),
                errorTable)));
        if (this.desktopMode) {
            children = errorTable;
        }
        return (React.createElement(AsperaWrapper, { className: `modal-body-app-info ${className ? className : ''}`, loading: loading },
            React.createElement(Button, { className: "app-info-download-button", type: "button", kind: "tertiary", onClick: downloadSupportData }, language.get('header.downloadSupportData')),
            children));
    }
}
